/**
 * @file      /src/schedmsg/schedmsg.js
 * @author    Ryan Rossiter, ryan@kingsds.network
 * @date      March 2020
 *
 * This is the client logic for subscribing to and handling SchedMsg events.
 * It inherits from PropagatingEventEmitter, and will register default event
 * callbacks for the commands, but they can be overridden by registering
 * additional event callbacks that return 'false'. This will prevent the default
 * behaviour from occuring.
 */
'use strict';

const { EventSubscriber } = require('dcp/events/event-subscriber');
const { PropagatingEventEmitter } = require('dcp/common/dcp-events');
const { setTimeout } = require('dcp/common/dcp-timers');

/** @typedef {import('../worker/index').Worker} Worker */

/**
 * SchedMsg is the class that will subscribe to receive commands from the scheduler.
 * These commands could be used to reload the worker, or broacast a message.
 *
 * SchedMsg extends the PropagatingEventEmitter.
 * When a command is received from the scheduler, it will be emitted on the Schedsg
 * instance. SchedMsg will register default handlers for these commands, but they can
 * be overridden by listening for the event and returning `false` from callback you provide.
 * @access public
 */
class SchedMsg extends PropagatingEventEmitter {
  /**
   * This command instructs the worker to immediately stop working, and can optionally disable the worker to prevent restarting. The user will need to manually intervene to restart the worker.
   *
   * @event SchedMsg#kill
   * @access public
   * @type {object}
   * @property {boolean} temporary When false, the worker will be disabled.
   *//**
   * This command instructs the worker to restart, e.g. call `worker.stop()` then `worker.start()`.
   *
   * @event SchedMsg#restart
   * @access public
   *//**
   * This command instructs the worker to stop working on a specific job.
   *
   * @event SchedMsg#remove
   * @access public
   * @type {object}
   * @property {string} jobId The id of the job to stop working on.
   *//**
   * This command is an announcement from the scheduler, the provided message should be displayed to the user (modal on web, console on node).
   *
   * @event SchedMsg#announce
   * @access public
   * @type {object}
   * @property {string} message The message to be displayed to the user.
   *//**
   * This command instructs the worker to "hard" reload, in the browser this will trigger a page refresh and in node it will exit the process.
   *
   * @event SchedMsg#reload
   * @access public
   *//**
   * This *web-only* command will open a new webpage to the provided URL.
   *
   * @event SchedMsg#openPopup
   * @access public
   * @type {object}
   * @property {string} href The URL to open the new page to.
   */

   /**
    * @constructor
    * @param {Worker} worker
    */
  constructor(worker) {
    super('SchedMsg');

    this.eventSubscriber = new EventSubscriber(this);

    this.worker = worker;
    this.handlers = {};
    this.pongAttempts = 0;

    this.registerHandler('kill', (opts) => this.onKill(opts));
    this.registerHandler('restart', (opts) => this.onRestart(opts));
    this.registerHandler('remove', (opts) => this.onRemove(opts));
    this.registerHandler('addPriorityJob', (opts) => this.onAddPriorityJob(opts));
    this.registerHandler('removePriorityJob', (opts) => this.onRemovePriorityJob(opts));
  }

  async start() {
    let eventRouterIdentity = this.eventSubscriber.eventRouterConnection.identity;
    if (!eventRouterIdentity) {
      await this.eventSubscriber.eventRouterConnection.identityPromise;
      eventRouterIdentity = this.eventSubscriber.eventRouterConnection.identity;
      //console.warn('SchedMsg: awaiting eventRouterIdentity', eventRouterIdentity.address);
    }
    const identityAddress = eventRouterIdentity.address;
    this.identityAddress = identityAddress;
    this.eventIntercepts = {}
    this.eventIntercepts['schedmsg::broadcast'] = this.onMessage.bind(this);
    this.eventIntercepts['schedmsg::command'] = this.onMessage.bind(this);

    await this.eventSubscriber.subscribeSchedmsg(['schedmsg::broadcast', 'schedmsg::command'], {
      filter: {
        $or: [
          {target: identityAddress},
          {target: this.worker.id},
          {broadcast:true},
        ],
      },
    });
  }

  async stop() {
    this.eventSubscriber.close();
  }

  registerHandler(command, callback) {
    if (this.listenerCount(command) > 0) {
      throw new Error(`There is a handler already registered for command '${command}'`);
    }

    this.on(command, callback);
  }

  onMessage(msg) {
    const { command, payload } = msg;

    if (this.listenerCount(command) > 0) {
      this.emit(command, payload);
    } else {
      console.warn(`No SchedMsg handler registered for command '${command}'`);
    }
  }

  onKill({ temporary }) {
    console.log(
      "!!!!!!!!!!!!!!!\n\n",
      "Kill command received from scheduler. Stopping worker...",
      "\n\n!!!!!!!!!!!!!!!");

    this.worker.stop(true);

    if (!temporary) {
      this.worker.constructor.disableWorker();
    }
  }

  onRestart() {
    throw new Error('not supported');
    setTimeout(() => {
      /* this can't work from inside schedmsg, needs application support to make a new instance of
       * DistributiveWorker
       */
    }, Math.random() * 10000 /* stagger workers coming back online */);
  }

  onRemove({ jobId }) {
    for (const sandbox of this.worker.workingSandboxes) {
      if (sandbox.jobId === jobId) {
        this.worker.returnSandbox(sandbox);
      }
    }
  }

  async onAddPriorityJob({ jobId, immediate }) {
    console.log('Received addPriorityJob schedmsg, adding job:', jobId);

    if (!Array.isArray(this.worker.config.jobIds))
      throw new Error('onAddPriorityJob: this.worker.config.jobIds must be an array.');

    this.worker.config.jobIds.push(jobId);

    if (immediate) {
      // return all slices that aren't in the priority job list
      await Promise.all(
        this.worker.slices.filter((slice) => !this.worker.config.jobIds.includes(slice.jobId))
          .map((slice) => this.worker.returnSlice(slice, 'SchedMsg'))
      ).catch((e) => {
        console.error("Error while returning slice before handling addPriorityJob schedmsg:");
        console.error(e);
      });

      // stop all current sandboxes that aren't working on priority slices
      for (const sandbox of this.worker.workingSandboxes) {
        if (!this.worker.config.jobIds.includes(sandbox.jobId)) {
          this.worker.returnSandbox(sandbox);
        }
      }
    }

    this.eventIntercepts['stop'] = () => {
      this.worker.config.jobIds =
      this.worker.config.jobIds.filter((id) => id !== jobId);
    }
    this.eventSubscriber.subscribe('job::optionalEvent', {
      filter: { job: jobId , eventName: 'stop' },
    })
    .catch((e) => {
      console.error("Failed to subscribe to stop event for addPriorityJob schedmsg:");
      console.error(e);
    });
  }

  async onRemovePriorityJob({ jobId, immediate }) {
    console.log('Received removePriorityJob schedmsg, removing job:', jobId);

    if (!Array.isArray(this.worker.config.jobIds))
      throw new Error('onRemovePriorityJob: this.worker.config.jobIds must be an array.');

    this.worker.config.jobIds =
      this.worker.config.jobIds.filter((id) => id !== jobId);

    if (immediate) {
      // return all slices belonging to this job
      await Promise.all(
        this.worker.slices.filter((slice) => slice.jobId === jobId)
          .map((slice) => this.worker.returnSlice(slice, 'SchedMsg'))
      ).catch((e) => {
        console.error("Error while returning slice for immediately removed job:");
        console.error(e);
      });

      // stop all current sandboxes that are working on this job
      for (const sandbox of this.worker.workingSandboxes) {
        if (sandbox.jobId === jobId) {
          this.worker.returnSandbox(sandbox);
        }
      }
    }
  }
}

exports.SchedMsg = SchedMsg;
